import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "../../api/api";
import toast from "react-hot-toast";
import gsap from "gsap";
import "react-quill-new/dist/quill.snow.css";

const HERO_IMAGE = "/assets/images/herobg.jpeg";
const FALLBACK_IMAGE = "/assets/images/children.webp";

const stripHtml = (html = "") =>
  String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const formatDate = (date) => {
  if (!date) return "Not published";

  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const getReadTime = (html = "") => {
  const words = stripHtml(html).split(" ").filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 220));
};

const LoadingState = () => (
  <main className="min-h-screen bg-slate-50 grid place-items-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#06183f] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-slate-500 font-medium">Loading amazing content...</p>
    </div>
  </main>
);

const NotFoundState = () => (
  <main className="min-h-screen bg-slate-50 grid place-items-center px-6">
    <div className="text-center">
      <h1 className="text-4xl md:text-5xl font-black text-slate-900">
        Post not found
      </h1>

      <p className="text-slate-600 mt-3">
        The article you&apos;re looking for doesn&apos;t exist or has been moved.
      </p>

      <Link
        to="/publications/news"
        className="inline-block mt-6 px-6 py-3 bg-[#06183f] text-white font-bold rounded-2xl hover:bg-[#ff6600] transition"
      >
        ← Back to News
      </Link>
    </div>
  </main>
);

const BlogHero = ({ post }) => (
  <section className="relative bg-white overflow-hidden">
    <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
      <img
        src={HERO_IMAGE}
        alt={post.title}
        className="absolute inset-0 w-full h-full object-cover"
      />

      <div className="absolute inset-0 bg-[#04160a]/80" />

      <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />

      <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />

      <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />

      <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

      <div className="relative max-w-7xl mx-auto px-6 text-center">
        <div className="hero-badge inline-flex items-center gap-3 mb-5">
          <span className="w-10 h-[3px] bg-[#ff6600]" />

          <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
            Published Article
          </span>
        </div>

        <h1
          className="hero-title text-4xl md:text-6xl lg:text-[76px] font-black text-white leading-[1.04] break-words"
          style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
        >
          {post.title}
        </h1>

        {post.excerpt && (
          <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px] break-words">
            {post.excerpt}
          </p>
        )}

        <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
          <Link to="/" className="hover:text-[#ff6600] transition-colors">
            Home
          </Link>

          <ChevronRight size={14} />

          <Link
            to="/publications/news"
            className="hover:text-[#ff6600] transition-colors"
          >
            News and Blog
          </Link>

          <ChevronRight size={14} />

          <span className="text-[#ff6600]">Single Post</span>
        </div>

        <div className="hero-text flex flex-wrap justify-center gap-3 mt-8">
          <span className="px-4 py-2 rounded-full bg-[#ff6600] text-white text-xs font-black uppercase tracking-widest">
            News
          </span>

          <span className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-xs font-bold">
            {getReadTime(post.content)} min read
          </span>

          <span className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-xs font-bold">
            {formatDate(post.published_at)}
          </span>
        </div>
      </div>
    </div>
  </section>
);

const BlogContent = ({ htmlContent }) => {
  const processedHtml = useMemo(() => {
    if (!htmlContent) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(String(htmlContent), "text/html");

    doc.body.innerHTML = doc.body.innerHTML
      .replaceAll("AK-SUBEB", '<span class="subeb-nowrap">AK-SUBEB</span>')
      .replaceAll("AKSUBEB", '<span class="subeb-nowrap">AKSUBEB</span>');

    doc.querySelectorAll("img").forEach((img) => {
      img.style.maxWidth = "100%";
      img.style.height = "auto";
      img.style.borderRadius = "24px";
      img.style.marginTop = "32px";
      img.style.marginBottom = "32px";
    });

    doc.querySelectorAll("table").forEach((table) => {
      table.style.width = "100%";
      table.style.maxWidth = "100%";
    });

    return doc.body.innerHTML;
  }, [htmlContent]);

  return (
    <div className="ql-snow w-full max-w-full min-w-0 overflow-hidden">
      <div
        className="
          ql-editor !p-0 w-full max-w-full min-w-0 overflow-hidden
          !text-[17px] md:!text-[18px] !leading-[1.8] !text-slate-700
          whitespace-normal [word-break:break-word] [overflow-wrap:break-word]
          [&_h1]:!text-4xl [&_h1]:!font-black [&_h1]:!text-[#06183f]
          [&_h2]:!text-3xl [&_h2]:!font-black [&_h2]:!text-[#06183f]
          [&_h3]:!text-2xl [&_h3]:!font-black [&_h3]:!text-[#06183f]
          [&_p]:!mb-8 [&_p]:!leading-[1.8] [&_p]:!text-slate-700
          [&_p]:whitespace-normal [&_p]:break-words
          [&_ul]:!my-8 [&_ol]:!my-8 [&_li]:!my-2 [&_li]:!leading-[1.8]
          [&_blockquote]:!my-10 [&_blockquote]:!border-l-[6px]
          [&_blockquote]:!border-[#ff6600] [&_blockquote]:!bg-orange-50
          [&_blockquote]:!rounded-2xl [&_blockquote]:!px-6 [&_blockquote]:!py-5
          [&_img]:!max-w-full [&_img]:!h-auto [&_img]:!rounded-3xl [&_img]:!my-10
          [&_iframe]:!w-full [&_iframe]:!max-w-full [&_iframe]:!rounded-3xl [&_iframe]:!my-10
          [&_a]:!text-[#06183f] [&_a]:!font-bold [&_a]:break-words
          [&_a]:hover:!text-[#ff6600] [&_table]:!w-full [&_table]:!max-w-full
          [&_td]:!border [&_td]:!p-3 [&_th]:!border [&_th]:!p-3
          [&_th]:!bg-slate-100 [&_pre]:!max-w-full [&_pre]:!overflow-x-auto
          [&_.subeb-nowrap]:whitespace-nowrap [&_.subeb-nowrap]:inline-block
        "
        dangerouslySetInnerHTML={{ __html: processedHtml }}
      />
    </div>
  );
};

const CommentForm = ({ postId, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const [comment, setComment] = useState({
    name: "",
    email: "",
    website: "",
    body: "",
    honeypot: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!postId) return;

    setLoading(true);

    try {
      await api.post("/comments", {
        ...comment,
        post_id: postId,
      });

      toast.success("Comment submitted for approval");

      setComment({
        name: "",
        email: "",
        website: "",
        body: "",
        honeypot: "",
      });

      onSuccess?.();
    } catch (err) {
      toast.error(err.cleanMessage || "Comment submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-[2rem] p-5 md:p-8 border border-slate-100 shadow-sm"
    >
      <h2 className="text-3xl font-black text-[#06183f] mb-2">
        Leave a Comment
      </h2>

      <p className="text-slate-500 mb-6">
        Your comment will be reviewed before it appears publicly.
      </p>

      <input
        type="text"
        name="honeypot"
        value={comment.honeypot}
        onChange={(e) => setComment({ ...comment, honeypot: e.target.value })}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
      />

      <div className="grid md:grid-cols-2 gap-4">
        <input
          required
          type="text"
          placeholder="Your name"
          value={comment.name}
          onChange={(e) => setComment({ ...comment, name: e.target.value })}
          className="border border-slate-200 rounded-2xl px-4 py-4 outline-none focus:border-[#06183f] transition"
        />

        <input
          required
          type="email"
          placeholder="Email address"
          value={comment.email}
          onChange={(e) => setComment({ ...comment, email: e.target.value })}
          className="border border-slate-200 rounded-2xl px-4 py-4 outline-none focus:border-[#06183f] transition"
        />
      </div>

      <input
        type="url"
        placeholder="Website (optional)"
        value={comment.website}
        onChange={(e) => setComment({ ...comment, website: e.target.value })}
        className="mt-4 w-full border border-slate-200 rounded-2xl px-4 py-4 outline-none focus:border-[#06183f] transition"
      />

      <textarea
        required
        rows={6}
        placeholder="Write your comment"
        value={comment.body}
        onChange={(e) => setComment({ ...comment, body: e.target.value })}
        className="mt-4 w-full border border-slate-200 rounded-2xl px-4 py-4 outline-none focus:border-[#06183f] transition"
      />

      <button
        type="submit"
        disabled={loading}
        className="mt-5 px-7 py-4 rounded-2xl bg-[#06183f] text-white font-black hover:bg-[#ff6600] transition disabled:opacity-60"
      >
        {loading ? "Submitting..." : "Submit Comment"}
      </button>
    </form>
  );
};

const BlogSinglePage = () => {
  const { slug } = useParams();

  const pageRef = useRef(null);

  const [post, setPost] = useState(null);
  const [latestPosts, setLatestPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPost = useCallback(async () => {
    setLoading(true);

    try {
      const res = await api.get(`/posts/single/${slug}`);
      const data = res.data;

      setPost(data);

      document.title = data.seo?.meta_title || data.title || "Blog Post";
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load post");
      setPost(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const loadSidebarData = useCallback(async () => {
    try {
      const [latestRes, categoriesRes, tagsRes] = await Promise.allSettled([
        api.get("/posts?status=published&limit=5"),
        api.get("/categories"),
        api.get("/tags"),
      ]);

      if (latestRes.status === "fulfilled") {
        const items =
          latestRes.value.data?.items ||
          latestRes.value.data?.data ||
          latestRes.value.data ||
          [];

        setLatestPosts(Array.isArray(items) ? items : []);
      }

      if (categoriesRes.status === "fulfilled") {
        setCategories(categoriesRes.value.data || []);
      }

      if (tagsRes.status === "fulfilled") {
        setAllTags(tagsRes.value.data || []);
      }
    } catch {
      //
    }
  }, []);

  useEffect(() => {
    loadPost();
    loadSidebarData();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [loadPost, loadSidebarData]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        delay: 0.25,
        ease: "power3.out",
      });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const postTags = useMemo(() => {
    if (Array.isArray(post?.tags)) return post.tags;

    if (typeof post?.tags === "string") {
      return post.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
    }

    return [];
  }, [post?.tags]);

  const postCategories = useMemo(() => {
    if (post?.categories?.length) return post.categories;

    if (post?.category_ids?.length && categories.length) {
      return categories.filter((cat) => post.category_ids.includes(cat.id));
    }

    return [];
  }, [post, categories]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: post.title,
          text: post.excerpt || stripHtml(post.content).slice(0, 120),
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Post link copied to clipboard");
      }
    } catch {
      toast.error("Unable to share post");
    }
  };

  const handlePrint = () => window.print();

  if (loading) return <LoadingState />;
  if (!post) return <NotFoundState />;

  return (
    <main ref={pageRef} className="bg-[#f7faf8] overflow-x-hidden">
      <BlogHero post={post} />

      <section className="py-14 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-10 items-start">
          <article className="min-w-0 w-full bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-5 md:p-12">
              <div className="flex flex-wrap items-center justify-between gap-4 pb-8 mb-10 border-b border-slate-100">
                <div>
                  <p className="text-sm font-black text-[#06183f] uppercase tracking-widest">
                    Published Article
                  </p>

                  <p className="text-slate-500 mt-2">
                    Updated {formatDate(post.updated_at || post.published_at)}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleShare}
                    className="px-5 py-3 rounded-2xl bg-[#06183f] text-white font-black hover:bg-[#ff6600] transition"
                  >
                    Share
                  </button>

                  <button
                    type="button"
                    onClick={handlePrint}
                    className="px-5 py-3 rounded-2xl bg-slate-100 text-slate-800 font-black hover:bg-slate-200 transition"
                  >
                    Print
                  </button>
                </div>
              </div>

              <BlogContent htmlContent={post.content} />

              {post.images?.length > 0 && (
                <div className="grid md:grid-cols-2 gap-5 mt-14">
                  {post.images.map((img, i) => (
                    <figure key={i}>
                      <img
                        src={img.url}
                        alt={img.alt || post.title}
                        className="rounded-3xl w-full h-80 object-cover shadow-sm"
                        loading="lazy"
                      />

                      {img.caption && (
                        <figcaption className="text-sm text-slate-500 mt-3">
                          {img.caption}
                        </figcaption>
                      )}
                    </figure>
                  ))}
                </div>
              )}

              <div className="mt-14 pt-8 border-t border-slate-100 flex flex-wrap gap-3">
                {postTags.length > 0 ? (
                  postTags.map((tag, idx) => (
                    <Link
                      key={idx}
                      to={`/publications/news?tag=${encodeURIComponent(tag)}`}
                      className="px-4 py-2 rounded-full bg-[#06183f]/10 text-[#06183f] text-sm font-black hover:bg-[#06183f] hover:text-white transition"
                    >
                      #{tag}
                    </Link>
                  ))
                ) : (
                  <p className="text-slate-500 text-sm">No tags attached.</p>
                )}
              </div>
            </div>

            {post.allow_comments && (
              <div className="bg-slate-50 p-5 md:p-12 border-t border-slate-100">
                <CommentForm postId={post.id} />
              </div>
            )}
          </article>

          <aside className="min-w-0 space-y-6 lg:sticky lg:top-28">
            <div className="bg-white rounded-[2rem] p-6 border border-slate-100 shadow-sm">
              <h3 className="text-xl font-black text-[#06183f] mb-4">
                Post Information
              </h3>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Reading Time</span>
                  <strong>{getReadTime(post.content)} min</strong>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Published</span>
                  <strong>{formatDate(post.published_at)}</strong>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Comments</span>
                  <strong>{post.allow_comments ? "Open" : "Closed"}</strong>
                </div>
              </div>
            </div>

            {postCategories.length > 0 && (
              <div className="bg-white rounded-[2rem] p-6 border border-slate-100 shadow-sm">
                <h3 className="text-xl font-black text-[#06183f] mb-4">
                  Categories
                </h3>

                <div className="space-y-2">
                  {postCategories.map((cat, idx) => (
                    <Link
                      key={cat.id || idx}
                      to={`/publications/news?category=${
                        cat.slug || cat.id || cat.name
                      }`}
                      className="block px-4 py-3 rounded-2xl bg-slate-50 hover:bg-[#06183f] hover:text-white transition font-bold"
                    >
                      {cat.name || cat}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {latestPosts.filter((p) => p.slug !== slug).length > 0 && (
              <div className="bg-white rounded-[2rem] p-6 border border-slate-100 shadow-sm">
                <h3 className="text-xl font-black text-[#06183f] mb-4">
                  Latest Posts
                </h3>

                <div className="space-y-4">
                  {latestPosts
                    .filter((item) => item.slug !== slug)
                    .slice(0, 4)
                    .map((item) => (
                      <Link
                        key={item.id}
                        to={`/publications/news/${item.slug}`}
                        className="flex gap-4 group"
                      >
                        <img
                          src={item.featured_image || FALLBACK_IMAGE}
                          alt={item.title}
                          className="w-20 h-20 object-cover rounded-2xl flex-shrink-0"
                          loading="lazy"
                        />

                        <div className="min-w-0">
                          <h4 className="font-black text-sm leading-snug group-hover:text-[#06183f] transition line-clamp-2">
                            {item.title}
                          </h4>

                          <p className="text-xs text-slate-500 mt-1">
                            {formatDate(item.published_at)}
                          </p>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            )}

            {allTags.length > 0 && (
              <div className="bg-white rounded-[2rem] p-6 border border-slate-100 shadow-sm">
                <h3 className="text-xl font-black text-[#06183f] mb-4">
                  Trending Tags
                </h3>

                <div className="flex flex-wrap gap-2">
                  {allTags.slice(0, 18).map((tag, idx) => (
                    <Link
                      key={tag.id || idx}
                      to={`/publications/news?tag=${encodeURIComponent(
                        tag.name || tag
                      )}`}
                      className="px-3 py-2 rounded-full bg-slate-100 text-slate-700 text-xs font-black hover:bg-[#ff6600] hover:text-white transition"
                    >
                      #{tag.name || tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>
    </main>
  );
};

export default BlogSinglePage;